import React from 'react';
import moment from 'moment';

import BaseComponent from 'src/components/BaseComponent';
import { Map, MCDatePicker } from 'src/components/complex';

import {
  countFormat,
  lang,
  number,
  permissions,
  remCss,
  withRouter
} from 'src/helpers';
import SlotCalendar from './SlotCalendar';
import LANG_CONST from '../../helpers/lang/const';
import { View } from '../basic';
import { StaticGoogleMap } from '../location/GoogleMaps';
import { remFontCss } from '../../pages/public/Clinic/utils';
import CertificateDetail from '../user/CertificateDetail';
import { MCText } from '../basic';

import { useDoctorDetail } from './useDoctorDetail';
import { getAvailableDayHandler } from './doctorDetailUtils';
import { LocationList, SpecialistChips, SubSpeciality } from './DoctorDetailParts';

export const DoctorRight = ({
  model,
  doctor,
  selectedDate,
  selectedDay,
  selectedMonth,
  slotsMonth = [],
  selectedLocation,
  onBookAppointment
}) => (
  <View style={{ gap: remCss(40), width: remCss(380) }}>
    {model ? (
      <SlotCalendar model={model} doctor={doctor} onBookAppointment={onBookAppointment} />
    ) : (
      <MCDatePicker
        style={{ padding: 0 }}
        selected={selectedDate}
        isSMedium={true}
        minDate={moment()}
        onChange={selectedDay}
        onSetMonth={selectedMonth}
        onClassNamesOfCell={getAvailableDayHandler(slotsMonth)}
      />
    )}
    {!!doctor.certificateFiles?.length && (
      <View style={{ gap: remCss(20) }}>
        <MCText
          label={LANG_CONST.titles.certificates}
          size={22}
          weight={600}
          style={{ marginTop: remCss(5) }}
        />
        <View style={{ gap: remCss(15) }}>
          {doctor.certificateFiles.map((item, i) => (
            <CertificateDetail item={item} key={i} />
          ))}
        </View>
      </View>
    )}
    {selectedLocation && (
      <StaticGoogleMap
        lat={selectedLocation.place?.location?.latitude}
        lng={selectedLocation.place?.location?.longitude}
      />
    )}
  </View>
);

const PriceRow = ({ label, value, currency }) => (
  <div className={'mc-flex'}>
    <div className={'mc-value-418 mc-grow'}>{label}</div>
    <div className={'mc-label-418'}>
      {number.format(value)} {currency}
    </div>
  </div>
);

const ChargeRows = ({ charge, currency }) =>
  charge ? (
    <>
      <PriceRow
        label={lang.t(LANG_CONST.constants.appointment_types.virtual)}
        value={charge.virtual}
        currency={currency}
      />
      <PriceRow
        label={lang.t(LANG_CONST.constants.appointment_types.in_office)}
        value={charge.office}
        currency={currency}
      />
    </>
  ) : null;

// Layout only. All data logic is in useDoctorDetail.
const DoctorDetailLayout = ({ doctor, model, onOpenDoctor }) => {
  const d = useDoctorDetail({ doctor, locations: doctor.hospitals || [] });

  return (
    <div className={'mc-container-content__body horizontal mc-doctor-detail'}>
      <div className={'mc-container-content__body-left mc-flex-25-col mc-b2r'}>
        <View style={{ gap: remCss(10) }}>
          <MCText label={LANG_CONST.speciality} size={18} weight={400} textLight />
          <SpecialistChips specialist={doctor.specialist} />
        </View>
        <SubSpeciality value={doctor.subSpeciality} fontSize={remFontCss} />
        <div className={'mc-flex-20-col mc-mr20 mc-mb20'}>
          <div className={'mc-value-622'}>{lang.t(LANG_CONST.clinic_hospital)}</div>
          <span className={'mc-label-418'}>{d.hospitalAddress}</span>
          <Map addressString={doctor.hospitalAddress} geocode={true} width={300} height={300} />
        </div>
      </div>

      <div className={'mc-container-content__body-center mc-flex-35-col mc-mlr20'}>
        <div className={'mc-flex-25-col'}>
          <div className={'mc-flex-20'}>
            <div className={'mc-flex-10-col mc-grow'}>
              <div className={'mc-label-416'}>{lang.t(LANG_CONST.total_treatments)}</div>
              <div className={'mc-value-618'}>{countFormat(doctor.treatmentCount)}</div>
            </div>
            <div className={'mc-flex-10-col mc-grow'}>
              <div className={'mc-label-416'}>{lang.t(LANG_CONST.total_patients)}</div>
              <div className={'mc-value-618'}>{countFormat((doctor.patientIds || []).length)}</div>
            </div>
            <div className={'mc-flex-10-col mc-grow'}>
              <div className={'mc-label-416'}>{lang.t(LANG_CONST.total_experience)}</div>
              <div className={'mc-value-618'}>{d.experience}</div>
            </div>
          </div>
          <pre className={'mc-label-418'} style={{ lineHeight: remCss(24) + 'px' }}>
            {doctor.description}
          </pre>
        </div>

        <div className={'mc-flex-20-col'}>
          <div className={'mc-value-622'}>{lang.t(LANG_CONST.titles.qualifications)}</div>
          {[
            [LANG_CONST.doctor_type, lang.t(LANG_CONST.constants.doctor_types[doctor.doctorType])],
            [LANG_CONST.institution, doctor.institution],
            [LANG_CONST.year_of_starting, doctor.yearOfStarting]
          ].map(([label, value]) => (
            <div className={'mc-flex'} key={label}>
              <div className={'mc-value-418 mc-grow'}>{lang.t(label)}</div>
              <div className={'mc-label-418'}>{value}</div>
            </div>
          ))}
        </div>

        {!!doctor.treatments?.length && (
          <div className={'mc-flex-20-col'}>
            <div className={'mc-value-622'}>{lang.t(LANG_CONST.titles.treatments)}</div>
            {doctor.treatments.map((item, i) => (
              <PriceRow
                key={i}
                label={item.name || item.title}
                value={item.fee || item.amount}
                currency={d.currency}
              />
            ))}
          </div>
        )}

        <div className={'mc-flex-15-col'}>
          <div className={'mc-value-622'}>{lang.t(LANG_CONST.consultant_charges)}</div>
          <div className={'mc-value-720'}>{lang.t(LANG_CONST.first_consultation)}</div>
          <ChargeRows charge={doctor.charge?.first} currency={d.currency} />
          <div className={'mc-value-720'}>
            {lang.t(LANG_CONST.text.follow_up, { attr: doctor.followupDuration || 30 })}
          </div>
          <ChargeRows charge={doctor.charge?.followup} currency={d.currency} />
          {d.chatTicketLabel && (
            <div className={'mc-flex mc-wrap'}>
              <div className={'mc-value-720 mc-grow'}>{lang.t(LANG_CONST.chat_ticket)}</div>
              <div className={'mc-label-418'}>{d.chatTicketLabel}</div>
            </div>
          )}
        </div>

        <div className={'mc-flex-col-15'}>
          {!!d.locations.length && (
            <>
              <div className={'mc-value-622'}>{lang.t(LANG_CONST.titles.locations)}</div>
              <LocationList
                locations={d.locations}
                selected={d.selectedLocation}
                onSelect={d.selectLocation}
                fontSize={remFontCss}
              />
            </>
          )}
          {d.selectedLocation && (
            <div style={{ maxWidth: remCss(380) }}>
              <StaticGoogleMap
                lat={d.selectedLocation.place?.location?.latitude}
                lng={d.selectedLocation.place?.location?.longitude}
              />
            </div>
          )}
        </div>
      </div>

      <div
        className={'mc-container-content__body-right mc-flex-25-col mc-b2l mc-pl20'}
        style={{ width: 'fit-content' }}
      >
        <div className={'mc-value-622'}>{lang.t(LANG_CONST.buttons.book_appointment)}</div>
        <DoctorRight doctor={doctor} model={model} onBookAppointment={onOpenDoctor} />
      </div>
    </div>
  );
};

// Thin container: only routing/navigation stays here (needs BaseComponent).
class DoctorDetail extends BaseComponent {
  openDoctor = (slot) => {
    const { doctor, model, onOpenDoctor } = this.props;
    if (onOpenDoctor) return onOpenDoctor(slot);
    if (slot && model.state.user?.isDoctor) return;
    let url = permissions.PAGES.DOCTORS_DETAIL_PAGE.match(doctor.username);
    if (slot) url = permissions.PAGES.APPOINTMENTS_BOOK_PAGE.match(doctor.username);
    this.navigate(url, { state: { slot }, isRedirect: true });
  };

  render() {
    const { doctor, model } = this.props;
    return <DoctorDetailLayout doctor={doctor} model={model} onOpenDoctor={this.openDoctor} />;
  }
}

export default withRouter(DoctorDetail);
